import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useAuth } from './context/AuthContext';
import api from './utils/api';
import LoginPage from './pages/LoginPage';
import BrandLogo from './components/BrandLogo';
import LandingPage from './pages/LandingPage';
import InvitePage from './pages/InvitePage';
import AccessPage from './pages/AccessPage';
import VerifactuVerifyPage from './pages/VerifactuVerifyPage';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import MobileBottomNav from './components/MobileBottomNav';
import { ImpersonationBanner } from './components/Impersonation';
import GlobalSearch from './components/GlobalSearch';
import AppTour from './components/AppTour';
import { TrialActivationGate } from './components/TrialActivationGate';
import { Card, Btn, ToastContainer, toast, EmptyState } from './components/UI';
import { Loading } from './components/UI';
import ErrorBoundary from './components/ErrorBoundary';
import { useIdleLogout } from './hooks/useIdleLogout';
import { Wrench, Lock } from 'lucide-react';
import { isSectionLocked, hasModule } from './utils/modules';
import * as Lu from 'lucide-react';

// Lazy-loaded page components (code splitting by role)
const SADashboard = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SADashboard })));
const SATenants = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SATenants })));
const SAUsers = lazy(() => import('./pages/superadmin/SAUsers').then(m => ({ default: m.SAUsers })));
const SAModules = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SAModules })));
const SAPlans = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SAPlans })));
const SASettings = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SASettings })));
const SACategories = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SACategories })));
const SAVehicleTypes = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SAVehicleTypes })));
const SATickets = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SATickets })));
const SAHealth = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SAHealth })));
const SAAuditLog = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SAAuditLog })));
const SATenantMetrics = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SATenantMetrics })));
const SASubscriptions = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SASubscriptions })));
const SAPricing = lazy(() => import('./pages/SuperAdminPages').then(m => ({ default: m.SAPricing })));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminRequests = lazy(() => import('./pages/admin/AdminRequests').then(m => ({ default: m.AdminRequests })));
const AdminCalendar = lazy(() => import('./pages/admin/AdminCalendar').then(m => ({ default: m.AdminCalendar })));
const AdminClients = lazy(() => import('./pages/admin/AdminClients').then(m => ({ default: m.AdminClients })));
const AdminProfessionals = lazy(() => import('./pages/admin/AdminProfessionals').then(m => ({ default: m.AdminProfessionals })));
const AdminResources = lazy(() => import('./pages/admin/AdminResources').then(m => ({ default: m.AdminResources })));
const AdminFleetMaintenance = lazy(() => import('./pages/admin/AdminFleetMaintenance').then(m => ({ default: m.AdminFleetMaintenance })));
const AdminInsurance = lazy(() => import('./pages/admin/AdminInsurance').then(m => ({ default: m.AdminInsurance })));
const AdminShifts = lazy(() => import('./pages/admin/AdminShifts').then(m => ({ default: m.AdminShifts })));
const AdminStock = lazy(() => import('./pages/admin/AdminStock').then(m => ({ default: m.AdminStock })));
const AdminPatientRecords = lazy(() => import('./pages/admin/AdminPatientRecords').then(m => ({ default: m.AdminPatientRecords })));
const AdminExecutive = lazy(() => import('./pages/admin/AdminExecutive').then(m => ({ default: m.AdminExecutive })));
const AdminCertifications = lazy(() => import('./pages/admin/AdminCertifications').then(m => ({ default: m.AdminCertifications })));
const AdminLeads = lazy(() => import('./pages/admin/AdminLeads').then(m => ({ default: m.AdminLeads })));
const AdminQuality = lazy(() => import('./pages/admin/AdminQuality').then(m => ({ default: m.AdminQuality })));
const AdminServices = lazy(() => import('./pages/admin/AdminServices').then(m => ({ default: m.AdminServices })));
const AdminBilling = lazy(() => import('./pages/admin/AdminBilling').then(m => ({ default: m.AdminBilling })));
const AdminReporting = lazy(() => import('./pages/admin/AdminReporting').then(m => ({ default: m.AdminReporting })));
const AdminRates = lazy(() => import('./pages/admin/AdminRates').then(m => ({ default: m.AdminRates })));
const AdminChecklists = lazy(() => import('./pages/admin/AdminChecklists').then(m => ({ default: m.AdminChecklists })));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings').then(m => ({ default: m.AdminSettings })));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers').then(m => ({ default: m.AdminUsers })));
const AdminCustomRoles = lazy(() => import('./pages/admin/AdminCustomRoles').then(m => ({ default: m.AdminCustomRoles })));
const AdminHospitals = lazy(() => import('./pages/admin/AdminHospitals').then(m => ({ default: m.AdminHospitals })));
const AdminDispatch = lazy(() => import('./pages/admin/AdminDispatch').then(m => ({ default: m.AdminDispatch })));
const AdminDispatchMap = lazy(() => import('./pages/admin/AdminDispatchMap').then(m => ({ default: m.AdminDispatchMap })));
const HospitalDashboard = lazy(() => import('./pages/HospitalPages').then(m => ({ default: m.HospitalDashboard })));
const HospitalPatients = lazy(() => import('./pages/HospitalPages').then(m => ({ default: m.HospitalPatients })));
const HospitalPatientDetail = lazy(() => import('./pages/HospitalPages').then(m => ({ default: m.HospitalPatientDetail })));
const HospitalServiceRequests = lazy(() => import('./pages/HospitalPages').then(m => ({ default: m.HospitalServiceRequests })));
const ClientDashboard = lazy(() => import('./pages/ClientPages').then(m => ({ default: m.ClientDashboard })));
const ClientRequests = lazy(() => import('./pages/ClientPages').then(m => ({ default: m.ClientRequests })));
const ClientQuotes = lazy(() => import('./pages/ClientPages').then(m => ({ default: m.ClientQuotes })));
const ClientInvoices = lazy(() => import('./pages/ClientPages').then(m => ({ default: m.ClientInvoices })));
const ProDashboard = lazy(() => import('./pages/ProfessionalPages').then(m => ({ default: m.ProDashboard })));
const ProServices = lazy(() => import('./pages/ProfessionalPages').then(m => ({ default: m.ProServices })));
const ProProfile = lazy(() => import('./pages/ProfessionalPages').then(m => ({ default: m.ProProfile })));
const ProSettlements = lazy(() => import('./pages/ProfessionalPages').then(m => ({ default: m.ProSettlements })));
const ProAvailability = lazy(() => import('./pages/ProfessionalPages').then(m => ({ default: m.ProAvailability })));
const ResourceDashboard = lazy(() => import('./pages/ResourcePages').then(m => ({ default: m.ResourceDashboard })));
const ResourcePatients = lazy(() => import('./pages/ResourcePages').then(m => ({ default: m.ResourcePatients })));
const ResourceHistory = lazy(() => import('./pages/ResourcePages').then(m => ({ default: m.ResourceHistory })));
const ResourceDispatch = lazy(() => import('./pages/ResourcePages').then(m => ({ default: m.ResourceDispatch })));
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));

const DEFAULT_SECTIONS = {
  superadmin: 'dashboard',
  admin: 'dashboard',
  client: 'dashboard',
  professional: 'dashboard',
  ambulance: 'dashboard',
  hospital: 'dashboard',
};

function PlaceholderPage({ title, onGoHome, description }) {
  return (
    <Card>
      <EmptyState
        icon={Wrench}
        title={title}
        description={description || 'Esta sección no está disponible para tu rol o aún está en desarrollo.'}
        action={onGoHome ? (
          <button onClick={onGoHome} style={{
            padding: '10px 20px', background: 'var(--accent)', color: 'var(--accent-on, #fff)',
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>← Volver al inicio</button>
        ) : null}
      />
    </Card>
  );
}

// Preload screen with progress bar
function PreloadScreen({ role }) {
  const [progress, setProgress] = useState(0);
  const [label, setLabel] = useState('Preparando datos...');

  useEffect(() => {
    let cancelled = false;
    const steps = [
      { pct: 20, text: 'Cargando panel...' },
      { pct: 50, text: 'Cargando datos...' },
      { pct: 80, text: 'Casi listo...' },
      { pct: 100, text: 'Listo' },
    ];
    
    // Quick progress simulation while actual preload runs
    let i = 0;
    const timer = setInterval(() => {
      if (cancelled || i >= steps.length) return;
      setProgress(steps[i].pct);
      setLabel(steps[i].text);
      i++;
    }, 300);

    return () => { cancelled = true; clearInterval(timer); };
  }, [role]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', gap: 20 }}>
      <BrandLogo size={48} />
      <div style={{ width: 260, height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${progress}%`, borderRadius: 2, background: 'var(--accent)', transition: 'width 0.3s ease' }} />
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}

export default function App() {
  const { user, loading, logout } = useAuth();

  const [section, setSectionRaw] = useState('dashboard');
  const [viewAs, setViewAs] = useState(null);
  // Hospital portal: paciente seleccionado para vista detalle.
  // Se resetea al cambiar de sección.
  const [hospitalPatientId, setHospitalPatientId] = useState(null);
  const setSection = (next) => { setHospitalPatientId(null); setSectionRaw(next); };
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [maintenance, setMaintenance] = useState(null);
  const [preloading, setPreloading] = useState(false);
  const [idleEnabled, setIdleEnabled] = useState(false);
  const preloaded = useRef(false);

  // Cargar el flag global enforce_idle_timeout desde /api/public-config.
  // Si SA lo desactiva, no aplicamos auto-logout. Si está activo (default true),
  // logout tras 30 min sin actividad — solo cuando hay user logueado.
  useEffect(() => {
    fetch('/api/public-config')
      .then(r => r.json())
      .then(c => setIdleEnabled(c?.enforce_idle_timeout !== false))
      .catch(() => setIdleEnabled(true));
  }, []);
  useIdleLogout(!!user && idleEnabled, () => {
    try { toast.info('Sesión cerrada', 'Has estado inactivo más de 30 minutos.'); } catch (_) {}
    logout();
  }, 30);

  useEffect(() => {
    if (user) {
      // Si el admin tiene custom role (allowed_sections), aterrizamos en la
      // primera sección permitida en lugar de 'dashboard' (que podría no estar).
      let landing = DEFAULT_SECTIONS[user.role] || 'dashboard';
      if (user.role === 'admin' && Array.isArray(user.allowed_sections) && user.allowed_sections.length > 0) {
        if (!user.allowed_sections.includes(landing)) landing = user.allowed_sections[0];
      }
      setSection(landing);
      setViewAs(null);
      
      // Preload data on first login
      if (!preloaded.current) {
        preloaded.current = true;
        setPreloading(true);
        const doPreload = async () => {
          try {
            if (user.role === 'admin') await api.preloadAdmin(user);
            else if (user.role === 'client') await api.preloadClient();
          } catch(e) { console.warn('Preload error:', e); }
          setPreloading(false);
        };
        doPreload();
      }
    } else {
      preloaded.current = false;
    }
  }, [user?.role]);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      const mobileRoles = ['client', 'professional', 'ambulance'];
      if (user && mobileRoles.includes(user.role)) {
        const dismissed = localStorage.getItem('_mc_install_dismissed');
        if (!dismissed) setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [user]);

  // Maintenance 503 catcher — cualquier request a la API que reciba
  // {code: MAINTENANCE_MODE} dispara este evento y pintamos banner full-page.
  // SA nunca ve esto porque el backend lo exceptúa.
  useEffect(() => {
    const handler = (e) => setMaintenance(e.detail || { message: 'Plataforma en mantenimiento' });
    window.addEventListener('platform:maintenance', handler);
    return () => window.removeEventListener('platform:maintenance', handler);
  }, []);

  // Handle Stripe Checkout return (success | cancelled). Webhook has already persisted
  // the subscription; we just show feedback + clean the query string.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const billing = params.get('billing');
    if (!billing) return;
    if (billing === 'success') {
      toast.success('Suscripción activa', 'Tarjeta registrada. Prueba gratis de 14 días iniciada.');
    } else if (billing === 'cancelled') {
      toast.info('Cancelado', 'No se ha registrado ninguna tarjeta. Puedes hacerlo cuando quieras desde Configuración.');
    }
    params.delete('billing');
    params.delete('session_id');
    const q = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (q ? '?' + q : ''));
  }, [user]);

  // ─── Early returns (AFTER all hooks) ───
  
  // PUBLIC ROUTES — always accessible, even when logged in
  const path = window.location.pathname;
  const goToApp = () => { window.history.pushState({}, '', '/login'); window.location.reload(); };
  const goToSignup = () => { window.history.pushState({}, '', '/login?view=signup_tenant'); window.location.reload(); };

  // /landing kept for backwards compatibility
  if (path === '/landing' || path === '/landing/') {
    return <ErrorBoundary><LandingPage onGoToApp={goToApp} onGoToSignup={goToSignup} /></ErrorBoundary>;
  }

  if (path.startsWith('/access/')) {
    const slug = path.replace('/access/', '').replace(/\/$/, '');
    if (slug) return <ErrorBoundary><AccessPage slug={slug} onGoToLogin={goToApp} /></ErrorBoundary>;
  }

  if (path.startsWith('/invite/')) {
    const token = path.replace('/invite/', '').replace(/\/$/, '');
    if (token) return <ErrorBoundary><InvitePage token={token} /></ErrorBoundary>;
  }

  // Verificación pública VeriFactu — el QR de las facturas apunta aquí.
  // Sin auth, solo lectura, expone solo número/fecha/total + huella.
  if (path.startsWith('/v/')) {
    return <ErrorBoundary><VerifactuVerifyPage /></ErrorBoundary>;
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
      </div>
    );
  }

  // Banner full-page de mantenimiento — bloquea la UI para usuarios no-SA cuando
  // el backend devuelve 503 MAINTENANCE_MODE. El SA nunca llega aquí porque el
  // middleware del backend lo exceptúa.
  if (maintenance && user?.role !== 'superadmin') {
    return (
      <>
      {/* Si el SA está suplantando a alguien de una empresa en mantenimiento, la
          franja le permite volver a su sesión (antes quedaba atrapado 1 h). */}
      <ImpersonationBanner />
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #F59E0B15, #EF444415)', padding: 24, textAlign: 'center',
      }}>
        <div style={{ maxWidth: 540, background: 'var(--card)', padding: 40, borderRadius: 16, border: '2px solid #F59E0B', boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
          <div style={{ marginBottom: 16, color: 'var(--gold)' }}><Lu.Wrench size={52} /></div>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 12, color: 'var(--text)' }}>
            Mantenimiento en curso
          </h1>
          <p style={{ fontSize: 15, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 24 }}>
            {maintenance.message || 'Estamos realizando tareas de mantenimiento. Volveremos en unos minutos.'}
          </p>
          {maintenance.scope === 'tenant' && (
            <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 16, padding: '8px 12px', background: 'var(--surface-alt)', borderRadius: 8 }}>
              Este mantenimiento afecta solo a tu organización. Contacta con tu administrador si tienes dudas.
            </div>
          )}
          <button
            onClick={() => { setMaintenance(null); window.location.reload(); }}
            style={{ padding: '10px 24px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Reintentar
          </button>
        </div>
      </div>
      </>
    );
  }

  // Public landing on root — only when not authenticated
  if (!user) {
    const isLoginRoute = path === '/login' || path === '/login/';
    if (!isLoginRoute) {
      return <ErrorBoundary><LandingPage onGoToApp={goToApp} onGoToSignup={goToSignup} /></ErrorBoundary>;
    }
    return <ErrorBoundary><LoginPage /></ErrorBoundary>;
  }

  // Si user autenticado intenta abrir /login → enviar a "/" para que vea su app.
  // Evita callejón sin salida (login form no funciona si ya tienes sesión).
  if (path === '/login' || path === '/login/' || path === '/landing') {
    window.history.replaceState({}, '', '/');
    // No reload — la app ya está cargada con su user, solo limpiamos la URL
  }

  // Show preloading screen on first login
  if (preloading) return <PreloadScreen role={user.role} />;

  // ─── Derived state (after hooks, after early returns) ───
  const effectiveRole = (user.role === 'superadmin' && viewAs) ? viewAs : user.role;

  const handleViewAsChange = (role) => {
    setViewAs(role);
    setSection(DEFAULT_SECTIONS[role || 'superadmin'] || 'dashboard');
  };

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') setShowInstallBanner(false);
    setInstallPrompt(null);
  };

  const dismissInstall = () => {
    setShowInstallBanner(false);
    localStorage.setItem('_mc_install_dismissed', '1');
  };

  // All roles get a mobile bottom nav when the sidebar is hidden (≤768px).
  // Kept as a set for clarity; any role with MOBILE_NAV config will render.
  const isMobileRole = ['client', 'professional', 'ambulance', 'admin', 'superadmin', 'hospital'].includes(effectiveRole);

  const renderPage = () => {
    // Universal profile page for all roles except professional (who has a specialized ProProfile)
    if (section === 'profile' && effectiveRole !== 'professional') return <Profile />;
    switch (effectiveRole) {
      case 'superadmin':
        switch (section) {
          case 'dashboard': return <SADashboard onNavigate={setSection} />;
          case 'tenants': return <SATenants onNavigate={setSection} />;
          case 'users': return <SAUsers />;
          case 'subscriptions': return <SASubscriptions onNavigate={setSection} />;
          case 'categories': return <SACategories />;
          case 'vehicleTypes': return <SAVehicleTypes />;
          case 'modules': return <SAModules />;
          case 'plans': return <SAPlans />;
          case 'pricing': return <SAPricing />;
          case 'tickets': return <SATickets />;
          case 'health': return <SAHealth />;
          case 'audit': return <SAAuditLog />;
          case 'tenantMetrics': return <SATenantMetrics onNavigate={setSection} />;
          case 'settings': return <SASettings />;
          default: return <SADashboard />;
        }
      case 'admin':
        // Sección de un módulo no contratado (también si se llega por un enlace
        // del panel, una notificación o la búsqueda): aviso en vez de la pantalla.
        if (isSectionLocked(user, section)) {
          return (
            <EmptyState
              icon={Lock}
              title="No incluido en tu plan"
              description="Esta función forma parte de un plan superior. Puedes revisar o mejorar tu suscripción en Configuración."
              action={<Btn onClick={() => setSection('settings')}>Ir a Configuración</Btn>}
            />
          );
        }
        switch (section) {
          case 'dashboard': return <AdminDashboard onNavigate={setSection} />;
          // Sin centralita, los transportes de hospitales no tendrían otra vista:
          // aquí se muestran todos los tipos con sus pestañas.
          case 'requests': return <AdminRequests defaultKind={hasModule(user, 'dispatch') ? 'drp' : null} />;
          case 'transport_requests': return <AdminRequests defaultKind="transport" />;
          case 'calendar': return <AdminCalendar />;
          case 'clients': return <AdminClients />;
          case 'rates': return <AdminRates />;
          case 'professionals': return <AdminProfessionals />;
          case 'resources': return <AdminResources onNavigate={setSection} />;
          case 'maintenance': return <AdminFleetMaintenance />;
          case 'insurance': return <AdminInsurance />;
          case 'shifts': return <AdminShifts />;
          case 'stock': return <AdminStock />;
          case 'patients': return <AdminPatientRecords />;
          case 'hospitals': return <AdminHospitals />;
          case 'dispatch': return <AdminDispatch />;
          case 'dispatch_map': return <AdminDispatchMap />;
          case 'executive': return <AdminExecutive onNavigate={setSection} />;
          case 'certifications': return <AdminCertifications />;
          case 'leads': return <AdminLeads />;
          case 'quality': return <AdminQuality />;
          case 'checklists': return <AdminChecklists />;
          case 'services': return <AdminServices />;
          case 'billing': return <AdminBilling />;
          case 'reporting': return <AdminReporting />;
          case 'users': return <AdminUsers />;
          case 'custom_roles': return <AdminCustomRoles />;
          case 'settings': return <AdminSettings />;
          default: return <AdminDashboard />;
        }
      case 'client':
        switch (section) {
          case 'dashboard': return <ClientDashboard />;
          case 'requests': return <ClientRequests />;
          case 'quotes': return <ClientQuotes />;
          case 'invoices': return <ClientInvoices />;
          default: return <ClientDashboard />;
        }
      case 'professional':
        switch (section) {
          case 'dashboard': return <ProDashboard />;
          case 'services': return <ProServices />;
          case 'availability': return <ProAvailability />;
          case 'profile': return <ProProfile />;
          case 'billing': return <ProSettlements />;
          default: return <ProDashboard />;
        }
      case 'ambulance':
        switch (section) {
          case 'dashboard': return <ResourceDashboard />;
          case 'dispatch': return <ResourceDispatch />;
          case 'patients': return <ResourcePatients />;
          case 'history': return <ResourceHistory />;
          default: return <ResourceDashboard />;
        }
      case 'hospital':
        switch (section) {
          case 'dashboard': return <HospitalDashboard />;
          case 'patients':
            // Detalle inline si hay paciente seleccionado; lista en otro caso.
            return hospitalPatientId
              ? <HospitalPatientDetail id={hospitalPatientId} onBack={() => setHospitalPatientId(null)} />
              : <HospitalPatients onOpenPatient={setHospitalPatientId} />;
          case 'service_requests': return <HospitalServiceRequests />;
          default: return <HospitalDashboard />;
        }
      default:
        return <PlaceholderPage
          title="Portal no disponible"
          description="No tienes acceso a esta sección con tu rol actual. Vuelve al inicio para ver lo que sí puedes hacer."
          onGoHome={() => setSection(DEFAULT_SECTIONS[effectiveRole] || 'dashboard')}
        />;
    }
  };

  return (
    <div className="app-layout">
      <ToastContainer />
      <Sidebar currentSection={section} onSectionChange={setSection} viewAs={viewAs} onViewAsChange={handleViewAsChange} />
      <div className="main-content">
        <ImpersonationBanner />
        {/* Aviso de prueba dentro del contenido (antes era hijo directo del layout
            flex y ocupaba una columna lateral). Oculto durante la suplantación:
            el SA no debe registrar tarjetas por el cliente. */}
        {user.role === 'admin' && !api.isImpersonating() && <TrialActivationGate />}
        <TopBar section={section} onNavigate={(sec) => setSection(sec)} />

        {showInstallBanner && (
          <div style={{
            padding: '10px 16px', background: 'var(--accent-dim)', borderBottom: '1px solid var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            fontSize: 13, flexWrap: 'wrap',
          }}>
            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>
              Instala MedCover Pro en tu dispositivo para acceso rápido
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={dismissInstall} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}>Ahora no</button>
              <Btn size="sm" onClick={handleInstall}>Instalar</Btn>
            </div>
          </div>
        )}

        <div className="page-content" key={`${effectiveRole}-${section}`}>
          {/* ErrorBoundary local: si una página concreta lanza error, no se cae toda
              la app — el sidebar/topbar siguen disponibles para navegar. */}
          <ErrorBoundary>
            <Suspense fallback={<Loading />}>{renderPage()}</Suspense>
          </ErrorBoundary>
        </div>
      </div>

      {isMobileRole && <MobileBottomNav currentSection={section} onSectionChange={setSection} />}
      {(effectiveRole === 'admin' || effectiveRole === 'superadmin') && <GlobalSearch onNavigate={setSection} />}
      {user.role === 'admin' && <AppTour />}
    </div>
  );
}
